import { useEffect, useState } from 'react'

import { api } from '@/lib/api'
import { targetsEngaged } from '@/lib/positions'
import type { Position } from '@/lib/types'

/** Relecture des positions : assez souvent pour suivre, assez rare pour ne pas
 *  réveiller la radio du téléphone en continu. */
const INTERVAL_MS = 4000

/**
 * Positions ouvertes, lues tant que l'écran qui s'en sert est affiché.
 *
 * `active` évite de charger les positions depuis l'onglet des réglages, où
 * personne ne les regarde.
 */
export function useOpenPositions(active: boolean): Position[] {
  const [positions, setPositions] = useState<Position[]>([])

  useEffect(() => {
    if (!active) return
    let stopped = false

    const read = () =>
      api
        .positions()
        .then((data) => {
          if (!stopped) setPositions(data)
        })
        // Un échec ponctuel ne doit pas vider la ligne : on garde le dernier
        // état connu plutôt que d'annoncer « aucune position » à tort.
        .catch(() => undefined)

    void read()
    const timer = window.setInterval(read, INTERVAL_MS)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [active])

  return active ? positions : []
}

/**
 * Bandeau des positions ouvertes, posé au-dessus du graphique.
 *
 * Ce que toutes les plateformes montrent en permanence : combien de positions
 * sont ouvertes et ce qu'elles valent à l'instant. Il fallait jusqu'ici
 * changer d'onglet pour le savoir — et donc perdre le graphique de vue — au
 * moment précis où l'on se demande s'il faut en ouvrir une de plus.
 *
 * Le montant est celui de la VUE STRATÉGIE, comme la page Positions : c'est le
 * résultat de ce qui a été saisi. La devise est celle que le serveur attache
 * aux positions, jamais une valeur saisie à la main.
 */
export function OpenPositionsBar({
  positions,
  onOpen,
  compact = false,
  cap = 0,
}: {
  positions: Position[]
  onOpen: () => void
  /** Variante sans cadre, pour la barre du haut sur téléphone. */
  compact?: boolean
  /** Plafond du total en jeu si toutes mes cibles étaient atteintes ; 0 : aucun. */
  cap?: number
}) {
  if (positions.length === 0) return null

  const total = positions.reduce((sum, position) => sum + Number(position.entry_profit ?? 0), 0)
  const currency = positions[0]?.account_currency ?? ''
  const lots = positions.reduce((sum, position) => sum + Number(position.lots), 0)
  const engage = targetsEngaged(positions)

  return (
    <button
      type="button"
      onClick={onOpen}
      className={
        compact
          ? 'flex min-w-0 items-center gap-2 rounded px-1 py-1 text-left active:bg-elevated'
          : 'flex min-h-[38px] w-full items-center gap-2 rounded-lg border border-border bg-panel px-3 text-left hover:bg-elevated/70 active:bg-elevated'
      }
    >
      <span className="shrink-0 whitespace-nowrap text-[11px] uppercase tracking-wide text-ink-muted">
        {positions.length} {compact ? 'pos.' : `position${positions.length > 1 ? 's' : ''}`}
      </span>
      {/* Le volume cumulé n'a pas sa place dans la barre du haut : sur
          375 px, il repousse le résultat hors de l'écran. */}
      {!compact && <span className="num text-[11px] text-ink-muted">{lots.toFixed(2)} lot</span>}

      {/* Marge restante sous le plafond : ce qui décide si une position de
          plus est possible. Au-delà de 80 %, la couleur le dit. */}
      {!compact && cap > 0 && (
        <span
          className={`num text-[11px] ${
            engage !== null && engage > cap * 0.8 ? 'text-warn' : 'text-ink-muted'
          }`}
        >
          cibles {engage === null ? '?' : engage.toFixed(0)} / {cap.toFixed(0)} {currency}
        </span>
      )}

      <span className="flex-1" />

      <span
        className={`num shrink-0 whitespace-nowrap font-semibold ${
          compact ? 'text-[12px]' : 'text-[14px]'
        } ${total >= 0 ? 'text-up' : 'text-down'}`}
      >
        {total >= 0 ? '+' : ''}
        {total.toFixed(2)} {currency}
      </span>
      <span className="shrink-0 text-[11px] text-ink-muted" aria-hidden>
        ›
      </span>
    </button>
  )
}
